import type { JSX } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { LogOut, Menu as MenuIcon, ShieldCheck, User } from 'lucide-react'
import { api } from '@/lib/api'
import { useUiStore } from '@/stores/ui'
import { useSessionStore } from '@/stores/session'
import { useConnectionStore, type WsStatus } from '@/stores/connection'
import { IconButton } from '@/ui/primitives/Button'
import { Menu } from '@/ui/primitives/Menu'
import { Select } from '@/ui/primitives/Select'
import { StatusDot, type StatusTone } from '@/ui/primitives/StatusDot'

/**
 * Верхняя панель. Это СТРОКА СЕТКИ каркаса, а не `position: sticky` — поэтому
 * она физически не может уехать вверх при скролле и наехать на карточки
 * (ровно это происходило в прошлой версии).
 *
 * Состояние WS переехало сюда из отдельного баннера: полоса «переподключение…»
 * над контентом сдвигала всю страницу вниз при каждом мигании сети. Здесь это
 * компактный чип, который меняется на месте и ничего не двигает.
 */

const WS_TONE: Record<WsStatus, StatusTone> = {
  idle: 'idle',
  connecting: 'busy',
  online: 'live',
  reconnecting: 'warn',
  offline: 'fail'
}

const WS_LABEL: Record<WsStatus, string> = {
  idle: 'нет подписки',
  connecting: 'подключаемся',
  online: 'живая связь',
  reconnecting: 'переподключаемся',
  offline: 'связь потеряна'
}

export function TopBar({ title }: { title: string }): JSX.Element {
  const navigate = useNavigate()
  const setNavOpen = useUiStore(state => state.setNavOpen)
  const user = useSessionStore(state => state.user)
  const memberships = useSessionStore(state => state.memberships)
  const activeOrgId = useSessionStore(state => state.activeOrgId)
  const setActiveOrg = useSessionStore(state => state.setActiveOrg)
  const clear = useSessionStore(state => state.clear)
  const wsStatus = useConnectionStore(state => state.status)
  const wsError = useConnectionStore(state => state.lastError)

  return (
    <header className="z-sticky flex h-14 min-w-0 items-center gap-3 border-b border-line bg-panel/70 px-4 backdrop-blur-xl sm:px-6">
      <IconButton label="Открыть меню" className="lg:hidden" onClick={() => setNavOpen(true)}>
        <MenuIcon aria-hidden className="size-5" />
      </IconButton>

      <h1 className="min-w-0 truncate font-display text-md font-semibold text-ink">{title}</h1>

      <div className="ml-auto flex min-w-0 items-center gap-2">
        <span
          className="flex shrink-0 items-center gap-2 rounded-pill border border-line bg-panel-2 px-2.5 py-1 text-2xs text-ink-muted"
          title={wsError ?? undefined}
        >
          <StatusDot
            tone={WS_TONE[wsStatus]}
            pulse={wsStatus === 'connecting' || wsStatus === 'reconnecting'}
          />
          <span className="hidden sm:inline">{WS_LABEL[wsStatus]}</span>
        </span>

        {memberships.length > 1 ? (
          <Select
            label="Организация"
            hideLabel
            value={activeOrgId ?? ''}
            onValueChange={setActiveOrg}
            options={memberships.map(membership => ({
              value: membership.orgId,
              label: membership.orgName
            }))}
            className="w-40"
          />
        ) : null}

        <Menu
          label={`Профиль: ${user?.displayName ?? '—'}`}
          trigger={
            <span className="press flex h-9 items-center gap-2 rounded-sm border border-line bg-panel-2 px-2.5 text-xs text-ink hover:border-line-strong">
              <span className="flex size-5 items-center justify-center rounded-pill bg-honey/16 text-honey">
                <User aria-hidden className="size-3" />
              </span>
              <span className="hidden max-w-28 truncate sm:inline">{user?.displayName ?? '—'}</span>
            </span>
          }
          items={[
            {
              label: 'Настройки и 2FA',
              icon: <ShieldCheck aria-hidden className="size-4" />,
              onSelect: () => void navigate({ to: '/settings' })
            },
            {
              label: 'Выйти',
              icon: <LogOut aria-hidden className="size-4" />,
              destructive: true,
              onSelect: () => {
                void api.auth.logout().finally(() => {
                  clear()
                  void navigate({ to: '/login' })
                })
              }
            }
          ]}
        />
      </div>
    </header>
  )
}
